// TechSystems.js - Purchasable airline technology systems
// CrewWise and WebLogic are tier-locked major purchases that provide ongoing benefits

// ============================================
// TECH SYSTEMS
// ============================================

export const TECH_SYSTEMS = {
  CREWWISE: {
    id: 'crewwise',
    name: 'CrewWise',
    tagline: 'Predictive Crew Legality System',
    description: 'AI-powered crew scheduling that predicts legality violations 2-6 hours before they occur. Prevents costly crew busts and last-minute cancellations.',
    
    // Acquisition
    purchasePrice: 18000000 * 100,  // $18M
    tierRequired: 3,
    
    // Ongoing
    monthlyMaintenance: 150000 * 100, // $150K/month
    
    // Effects
    effects: {
      crewBustPrevention: 0.85,      // 85% of potential crew busts caught early
      crewBustWarningHours: 4,        // Average 4 hours warning
      autoReassignment: true,         // Can auto-swap crew when violation detected
      legalityTracking: 'realtime',   // Real-time vs daily tracking
      fatigueModeling: true,          // Predicts fatigue-based issues
    },
    
    // What it does in gameplay
    gameplay: {
      preventsCrewCancellations: true,
      showsCrewStatusDashboard: true,
      alertsBeforeViolation: true,
      reducesCrewRelatedDelays: 0.6,  // 60% reduction
    },
  },

  WEBLOGIC: {
    id: 'weblogic',
    name: 'WebLogic',
    tagline: 'Network Optimization Engine',
    description: 'Eliminates deadheading through intelligent crew positioning. Automatically repositions crew on your flights instead of buying commercial tickets.',
    
    // Acquisition
    purchasePrice: 22000000 * 100,  // $22M
    tierRequired: 3,
    
    // Ongoing
    monthlyMaintenance: 180000 * 100, // $180K/month
    
    // Effects
    effects: {
      deadheadElimination: 0.90,      // 90% of deadhead eliminated
      crewPositioning: 'automatic',    // Auto-positions crew
      networkOptimization: true,       // Optimizes crew flow across network
      costReduction: 0.25,            // 25% reduction in crew repositioning costs
    },
    
    // What it does in gameplay
    gameplay: {
      reducesDeadheadCosts: true,
      autoPositionsCrew: true,
      showsCrewFlowMap: true,
      reducesCrewShortages: 0.7,      // 70% fewer crew shortage events
    },
  },

  YIELDMASTER: {
    id: 'yieldmaster',
    name: 'YieldMaster Pro',
    tagline: 'Dynamic Pricing Engine',
    description: 'Real-time yield management that adjusts ticket prices based on demand, competitor pricing, and booking curves.',
    
    // Acquisition
    purchasePrice: 12000000 * 100,  // $12M
    tierRequired: 2,
    
    // Ongoing
    monthlyMaintenance: 100000 * 100, // $100K/month
    
    // Effects
    effects: {
      revenueOptimization: 0.15,      // +15% revenue optimization
      demandForecasting: true,         // See demand before committing
      competitorTracking: true,        // Track competitor pricing
      dynamicPricing: true,            // Prices adjust in real-time
    },
    
    gameplay: {
      increasesRevenue: 0.15,
      showsDemandForecast: true,
      showsCompetitorPrices: true,
      reducesEmptySeats: 0.20,        // 20% better load factors
    },
  },

  MAINTENANCE_PRO: {
    id: 'maintenance_pro',
    name: 'MaintenancePro',
    tagline: 'Predictive Maintenance System',
    description: 'IoT sensors and AI predict component failures before they happen. Reduces AOG events and maintenance costs.',
    
    // Acquisition
    purchasePrice: 8000000 * 100,   // $8M
    tierRequired: 2,
    
    // Ongoing
    monthlyMaintenance: 80000 * 100, // $80K/month
    
    // Effects
    effects: {
      aogPrevention: 0.70,            // 70% fewer AOG events
      maintenanceCostReduction: 0.20, // 20% lower maintenance costs
      predictiveAlerts: true,          // Alerts before failures
      partInventoryOptimization: true, // Better parts stocking
    },
    
    gameplay: {
      reducesGroundings: 0.70,
      reducesMaintCosts: 0.20,
      showsAircraftHealth: true,
      preventsInFlightFailures: true,
    },
  },
};

// ============================================
// TECH STATE MANAGEMENT
// ============================================

export function createTechState() {
  return {
    owned: {},              // { crewwise: { purchasedAt, monthsPaid } }
    totalMonthlyMaintenance: 0,
  };
}

export function canPurchaseTech(techState, techId, cash, tier) {
  const tech = TECH_SYSTEMS[techId.toUpperCase()];
  if (!tech) {
    return { canPurchase: false, reason: 'Unknown technology' };
  }
  
  // Already own it?
  if (techState.owned[tech.id]) {
    return { canPurchase: false, reason: 'Already owned' };
  }
  
  // Tier check
  if (tier < tech.tierRequired) {
    return { 
      canPurchase: false, 
      reason: `Requires Tier ${tech.tierRequired}`,
      tierRequired: tech.tierRequired,
    };
  }
  
  // Cash check
  if (cash < tech.purchasePrice) {
    return { 
      canPurchase: false, 
      reason: 'Insufficient funds',
      shortfall: tech.purchasePrice - cash,
    };
  }
  
  return { 
    canPurchase: true, 
    cost: tech.purchasePrice,
    monthlyMaintenance: tech.monthlyMaintenance,
  };
}

export function purchaseTech(techState, techId, gameDay) {
  const tech = TECH_SYSTEMS[techId.toUpperCase()];
  if (!tech) {
    return { success: false, error: 'Unknown technology' };
  }
  
  const newState = {
    ...techState,
    owned: {
      ...techState.owned,
      [tech.id]: {
        ...tech,
        purchasedAt: gameDay,
        monthsPaid: 0,
      },
    },
  };
  
  newState.totalMonthlyMaintenance = calculateTotalMaintenance(newState.owned);
  
  return {
    success: true,
    state: newState,
    cost: tech.purchasePrice,
    message: `${tech.name} is now operational. ${tech.tagline}`,
  };
}

export function processMonthlyTechCosts(techState) {
  const costs = [];
  const newOwned = { ...techState.owned };
  
  Object.entries(techState.owned).forEach(([id, tech]) => {
    costs.push({
      id,
      name: tech.name,
      cost: tech.monthlyMaintenance,
    });
    newOwned[id] = {
      ...tech,
      monthsPaid: tech.monthsPaid + 1,
    };
  });
  
  return {
    state: { ...techState, owned: newOwned },
    totalCost: techState.totalMonthlyMaintenance,
    breakdown: costs,
  };
}

function calculateTotalMaintenance(owned) {
  return Object.values(owned).reduce((sum, tech) => sum + tech.monthlyMaintenance, 0);
}

// ============================================
// EFFECT CHECKERS - Use in game systems
// ============================================

export function hasTech(techState, techId) {
  return !!techState?.owned?.[techId];
}

export function getTechEffect(techState, techId, effectName) {
  const tech = techState?.owned?.[techId];
  if (!tech) return null;
  return tech.effects?.[effectName];
}

// CrewWise specific
export function getCrewBustPreventionChance(techState) {
  return getTechEffect(techState, 'crewwise', 'crewBustPrevention') || 0;
}

export function hasCrewWise(techState) {
  return hasTech(techState, 'crewwise');
}

// WebLogic specific
export function getDeadheadReduction(techState) {
  return getTechEffect(techState, 'weblogic', 'deadheadElimination') || 0;
}

export function hasWebLogic(techState) {
  return hasTech(techState, 'weblogic');
}

// YieldMaster specific
export function getRevenueBoost(techState) {
  return getTechEffect(techState, 'yieldmaster', 'revenueOptimization') || 0;
}

// MaintenancePro specific
export function getAOGPreventionChance(techState) {
  return getTechEffect(techState, 'maintenance_pro', 'aogPrevention') || 0;
}

export function getMaintenanceCostReduction(techState) {
  return getTechEffect(techState, 'maintenance_pro', 'maintenanceCostReduction') || 0;
}

// ============================================
// EXPORTS
// ============================================

export default {
  TECH_SYSTEMS,
  createTechState,
  canPurchaseTech,
  purchaseTech,
  processMonthlyTechCosts,
  hasTech,
  getTechEffect,
  getCrewBustPreventionChance,
  hasCrewWise,
  getDeadheadReduction,
  hasWebLogic,
  getRevenueBoost,
  getAOGPreventionChance,
  getMaintenanceCostReduction,
};
